import type {TokenClaimsSerializer} from "@/generated/DjangoTypes";
import {useTrialTabMilestone} from "@/hooks/useTrialTabMilestone";
import SearchVisibility from "@/pages/SearchVisibility";
import useTokenStore from "@/store/token";
import {fetchCompany} from "@/utils/loaderUtils";
import {createFileRoute, redirect} from "@tanstack/react-router";
import {zodValidator} from "@tanstack/zod-adapter";
import {jwtDecode} from "jwt-decode";
import {z} from "zod";

export const Route = createFileRoute("/_authenticated/_company/company/$companyId/search-visibility")({
  component: RouteComponent,
  params: {
    parse: raw => ({companyId: +raw.companyId}),
    stringify: params => ({companyId: params.companyId?.toString()}),
  },
  validateSearch: zodValidator(
    z.object({
      addCountry: z.boolean().optional(),
      tab: z.enum(["competitors", "keywords", "opportunities", "overview"]).catch("overview"),
    }),
  ),
  loaderDeps: ({search}) => ({addCountry: search.addCountry, tab: search.tab}),
  loader: async ({params, deps}) => {
    const token = useTokenStore.getState().access;

    if (!token) {
      throw redirect({to: "/login", replace: true});
    }

    const claims = jwtDecode<TokenClaimsSerializer>(token);
    const isAdmin = claims.access_level === "COMPANY_ADMIN" || claims.access_level === "INFLUENCER_ADMIN";
    const isAgency = claims.access_level === "AGENCY";

    if (!isAdmin && !isAgency) {
      return;
    }

    const company = await fetchCompany(params.companyId);

    if (company.agency?.multiple_brands) {
      throw redirect({
        to: "/agency/$agencyId/subbrand/$companyId/search-visibility",
        params: {agencyId: company.agency.id, companyId: company.id},
        search: {addCountry: deps.addCountry, tab: deps.tab},
        replace: true,
      });
    }

    return;
  },
});

function RouteComponent() {
  const {companyId} = Route.useParams();
  const {tab} = Route.useSearch();
  useTrialTabMilestone("search_tab");
  return <SearchVisibility companyProfileId={companyId} tab={tab} />;
}
